import React, { useState, useEffect, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import {
  Box,
  Typography,
  Button,
  TextField,
  Modal,
  CircularProgress,
  Snackbar,
  Alert,
  Card,
  Chip,
  IconButton,
  Dialog,
  DialogContent,
  Grid,
} from "@mui/material";
import AddCommentIcon from "@mui/icons-material/AddComment";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DescriptionIcon from "@mui/icons-material/Description";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import api, { API_BASE_URL } from "../../../utils/api";

const ArchitecturalRequestComments = ({
  architectural_request_id,
  currentUserId,
  refreshTrigger,
}) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [fileError, setFileError] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [previewFile, setPreviewFile] = useState({ open: false, url: "", name: "" });

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const softInsetSx = {
    borderRadius: "14px",
    border: "1px solid rgba(47,99,173,0.08)",
    backgroundColor: "rgba(47,99,173,0.03)",
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const dialogPaperSx = {
    borderRadius: "18px",
    background: "rgba(255,255,255,0.98)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
  };

  const fetchComments = useCallback(async () => {
    if (!architectural_request_id) return;
    setIsLoading(true);
    try {
      const { data } = await api.get(
        `/getExternalArchitecturalComments/${architectural_request_id}`
      );
      setComments(data || []);
    } catch (err) {
      console.error("Failed to fetch architectural comments:", err);
    } finally {
      setIsLoading(false);
    }
  }, [architectural_request_id]);

  const fetchFiles = useCallback(async () => {
    try {
      const { data } = await api.get(`/request-files/${architectural_request_id}`);
      setUploadedFiles(data || []);
    } catch (err) {
      console.error("Failed to fetch files:", err);
    }
  }, [architectural_request_id]);

  useEffect(() => {
    fetchComments();
    fetchFiles();
  }, [fetchComments, fetchFiles, refreshTrigger]);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setSelectedFile(file);
      setFilePreview(
        file.type.startsWith("image") ? URL.createObjectURL(file) : null
      );
      setFileError("");
    }
  }, []);

  const onDropRejected = () =>
    setFileError("Only PDF, DOCX, and image files are allowed.");

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    onDropRejected,
    accept: {
      "application/pdf": [".pdf"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
        ".docx",
      ],
      "image/*": [".png", ".jpg", ".jpeg"],
    },
    multiple: false,
  });

  const uploadFileToServer = async (commentId) => {
    if (!selectedFile) return null;

    const formData = new FormData();
    formData.append("request_id", architectural_request_id);
    formData.append("upload_urls", selectedFile);
    formData.append("uploaded_by", currentUserId);
    formData.append("comment_id", commentId);

    try {
      await api.post(`/request-files`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return true;
    } catch (err) {
      console.error("Failed to upload file:", err);
      return false;
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      alert("Comment cannot be empty.");
      return;
    }
    if (fileError) {
      alert("Please fix the file error before submitting.");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        architectural_request_id,
        user_id: currentUserId,
        comment_text: newComment,
        is_internal: 0,
      };

      const { data } = await api.post(
        `/addArchitecturalRequestComments`,
        payload
      );
      const newCommentId = data.comment_id;

      if (selectedFile) {
        const success = await uploadFileToServer(newCommentId);
        if (!success) {
          alert("Comment saved, but file upload failed. Please retry later.");
        }
      }

      await fetchComments();
      await fetchFiles();

      setNewComment("");
      setSelectedFile(null);
      setFilePreview(null);
      setFileError("");
      setIsModalOpen(false);
      setShowConfirm(true);
    } catch (err) {
      console.error("Failed to add comment:", err);
      alert("Failed to add comment. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFilesForComment = (commentId) =>
    uploadedFiles.filter((file) => file.comment_id === commentId);

  const getFileUrl = (file) => {
    const filename = (file.file_url || "").split("/").pop();
    return `${API_BASE_URL}/request-images/${encodeURIComponent(filename)}`;
  };

  const getFileExtension = (fileName = "") =>
    fileName.split(".").pop()?.toLowerCase() || "";

  const isImageFile = (fileName = "") =>
    ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(getFileExtension(fileName));

  const isPdfFile = (fileName = "") => getFileExtension(fileName) === "pdf";

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: "180px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Card sx={{ ...pageCardSx, overflow: "hidden" }}>
        <Box
          sx={{
            px: { xs: 1.5, sm: 2, md: 3 },
            pt: 2.25,
            pb: 1.5,
            borderBottom: "1px solid rgba(47,99,173,0.08)",
            backgroundColor: "rgba(47,99,173,0.02)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <ChatBubbleOutlineIcon sx={{ color: "#2f63ad" }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
              Comments
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            Review architectural request comment history and attachments.
          </Typography>
        </Box>

        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mb: 2.5,
            }}
          >
            <Button
              variant="contained"
              startIcon={<AddCommentIcon />}
              onClick={() => setIsModalOpen(true)}
              sx={primaryButtonSx}
            >
              Add Comment
            </Button>
          </Box>

          {comments.length > 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {comments.map((comment) => {
                const commentFiles = getFilesForComment(
                  comment.id || comment.comment_id
                );
                const timestamp = new Date(comment.sent_at);

                return (
                  <Card
                    key={comment.comment_id}
                    sx={{
                      borderRadius: "16px",
                      border: "1px solid rgba(47,99,173,0.08)",
                      boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
                      backgroundColor: "#fff",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        px: 2,
                        py: 1.5,
                        borderBottom: "1px solid rgba(47,99,173,0.08)",
                        backgroundColor: "rgba(47,99,173,0.05)",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1.5,
                        flexWrap: "wrap",
                      }}
                    >
                      <Chip
                        label="External Comment"
                        size="small"
                        sx={{
                          fontWeight: 700,
                          borderRadius: "999px",
                          color: "#1d4ed8",
                          backgroundColor: "rgba(59,130,246,0.12)",
                        }}
                      />
                    </Box>

                    <Box sx={{ p: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#223142",
                          whiteSpace: "pre-wrap",
                          mb: commentFiles.length > 0 ? 1.25 : 0,
                        }}
                      >
                        {comment.comment_text}
                      </Typography>

                      {commentFiles.length > 0 && (
                        <Box sx={{ mt: 1.25 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, color: "#223142", mb: 1 }}
                          >
                            Attached File(s)
                          </Typography>

                          <Grid container spacing={2}>
                            {commentFiles.map((file, idx) => {
                              const fileUrl = getFileUrl(file);
                              const isImage = isImageFile(file.file_name);
                              const isPdf = isPdfFile(file.file_name);

                              return (
                                <Grid item xs={12} sm={6} md={4} key={idx}>
                                  <Card
                                    sx={{
                                      ...softInsetSx,
                                      p: 2,
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      gap: 1,
                                      textAlign: "center",
                                      height: "100%",
                                      backgroundColor: "#fff",
                                    }}
                                  >
                                    {isImage ? (
                                      <>
                                        <Box
                                          component="img"
                                          src={fileUrl}
                                          alt={file.file_name}
                                          sx={{
                                            maxWidth: "100%",
                                            maxHeight: 120,
                                            borderRadius: "8px",
                                            cursor: "pointer",
                                            objectFit: "cover",
                                          }}
                                          onClick={() =>
                                            setPreviewFile({
                                              open: true,
                                              url: fileUrl,
                                              name: file.file_name,
                                            })
                                          }
                                        />
                                        <Typography
                                          variant="body2"
                                          sx={{ mt: 0.5, wordBreak: "break-word" }}
                                        >
                                          {file.file_name}
                                        </Typography>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          startIcon={<VisibilityIcon />}
                                          onClick={() =>
                                            setPreviewFile({
                                              open: true,
                                              url: fileUrl,
                                              name: file.file_name,
                                            })
                                          }
                                          sx={secondaryButtonSx}
                                        >
                                          Preview
                                        </Button>
                                      </>
                                    ) : isPdf ? (
                                      <>
                                        <PictureAsPdfIcon
                                          color="error"
                                          sx={{ fontSize: 42 }}
                                        />
                                        <Typography
                                          variant="body2"
                                          sx={{ mt: 0.5, wordBreak: "break-word" }}
                                        >
                                          {file.file_name}
                                        </Typography>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          startIcon={<VisibilityIcon />}
                                          onClick={() =>
                                            setPreviewFile({
                                              open: true,
                                              url: fileUrl,
                                              name: file.file_name,
                                            })
                                          }
                                          sx={secondaryButtonSx}
                                        >
                                          View PDF
                                        </Button>
                                      </>
                                    ) : (
                                      <>
                                        <InsertDriveFileIcon
                                          sx={{ fontSize: 42, color: "#2f63ad" }}
                                        />
                                        <Typography
                                          variant="body2"
                                          sx={{ wordBreak: "break-word" }}
                                        >
                                          {file.file_name}
                                        </Typography>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          component="a"
                                          href={fileUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          sx={secondaryButtonSx}
                                        >
                                          Open
                                        </Button>
                                      </>
                                    )}
                                  </Card>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </Box>
                      )}

                      <Box
                        sx={{
                          my: 1.5,
                          borderTop: "1px solid rgba(47,99,173,0.08)",
                        }}
                      />

                      <Typography
                        variant="caption"
                        sx={{
                          color: "#6b778c",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          flexWrap: "wrap",
                        }}
                      >
                        <AccessTimeIcon sx={{ fontSize: 14 }} />
                        Added by {comment.user_name} {comment.user_lastname || ""} on{" "}
                        {timestamp.toLocaleDateString()} at{" "}
                        {timestamp.toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Typography>
                    </Box>
                  </Card>
                );
              })}
            </Box>
          ) : (
            <Card sx={{ ...pageCardSx, p: 3, textAlign: "center" }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, color: "#223142" }}
              >
                No comments yet
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#6b778c", mt: 0.75 }}
              >
                Add the first comment for this architectural request.
              </Typography>

              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<AddCommentIcon />}
                  onClick={() => setIsModalOpen(true)}
                  sx={primaryButtonSx}
                >
                  Add Comment
                </Button>
              </Box>
            </Card>
          )}
        </Box>
      </Card>

      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "92%", sm: 520 },
            maxHeight: "90vh",
            overflowY: "auto",
            backgroundColor: "rgba(255,255,255,0.98)",
            padding: 0,
            borderRadius: "18px",
            boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
            border: "1px solid rgba(47,99,173,0.08)",
          }}
        >
          <Box
            sx={{
              px: 3,
              pt: 2.5,
              pb: 1.5,
              borderBottom: "1px solid rgba(47,99,173,0.08)",
              backgroundColor: "rgba(47,99,173,0.02)",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
              Add Comment
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.5 }}>
              Add a comment and optionally attach a PDF, DOCX, or image.
            </Typography>
          </Box>

          <Box sx={{ p: 3 }}>
            <TextField
              fullWidth
              label="Comment"
              multiline
              rows={3}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              sx={{ ...textFieldSx, mb: 2 }}
            />

            <Box
              {...getRootProps()}
              sx={{
                ...softInsetSx,
                p: 2.5,
                textAlign: "center",
                cursor: "pointer",
                borderStyle: "dashed",
                borderWidth: "2px",
                mb: 2,
              }}
            >
              <input {...getInputProps()} />
              <CloudUploadIcon sx={{ fontSize: 40, color: "#1976d2", mb: 1 }} />
              <Typography variant="body2" sx={{ color: "#6b778c" }}>
                Drag & drop a PDF, DOCX, or image file, or click to select
              </Typography>
            </Box>

            {fileError && (
              <Typography sx={{ color: "red", fontSize: 14, mb: 2 }}>
                {fileError}
              </Typography>
            )}

            {selectedFile && !filePreview && (
              <Card sx={{ ...softInsetSx, p: 2, mb: 2, backgroundColor: "#fff" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <DescriptionIcon sx={{ color: "#2f63ad" }} />
                  <Typography variant="body2" sx={{ color: "#223142", fontWeight: 600 }}>
                    {selectedFile.name}
                  </Typography>
                </Box>
              </Card>
            )}

            {filePreview && (
              <Box sx={{ textAlign: "center", mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, color: "#223142" }}>
                  Preview
                </Typography>
                <img
                  src={filePreview}
                  alt="Preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: 150,
                    borderRadius: 8,
                  }}
                />
              </Box>
            )}

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.25 }}>
              <Button
                variant="outlined"
                onClick={() => setIsModalOpen(false)}
                sx={secondaryButtonSx}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                onClick={handleAddComment}
                disabled={isSubmitting}
                sx={primaryButtonSx}
              >
                {isSubmitting ? "Saving..." : "Save Comment"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Dialog
        open={previewFile.open}
        onClose={() => setPreviewFile({ open: false, url: "", name: "" })}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogContent
          sx={{
            position: "relative",
            backgroundColor: "#111",
            p: 2,
            textAlign: "center",
          }}
        >
          <IconButton
            onClick={() => setPreviewFile({ open: false, url: "", name: "" })}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              zIndex: 1,
              bgcolor: "rgba(0,0,0,0.45)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
            }}
          >
            <CloseIcon sx={{ color: "white" }} />
          </IconButton>

          {isPdfFile(previewFile.name) ? (
            <object
              data={previewFile.url}
              type="application/pdf"
              width="100%"
              height="85vh"
              style={{ border: "none", borderRadius: "10px", background: "white" }}
            />
          ) : (
            <Box
              component="img"
              src={previewFile.url}
              alt={previewFile.name}
              sx={{
                maxWidth: "100%",
                maxHeight: "85vh",
                objectFit: "contain",
                borderRadius: "10px",
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={showConfirm}
        autoHideDuration={3000}
        onClose={() => setShowConfirm(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" variant="filled" sx={{ width: "100%", borderRadius: 3 }}>
          Comment added successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ArchitecturalRequestComments;